/**
 * OrgStructureCard — Organisationsstruktur (Variante C, Phase 5).
 * Ebenen verwalten (tiefere Ebenen hinzufügen/entfernen) + Org-Baum ansehen und
 * manuelle Untereinheiten (z. B. Abteilungen/Teams) anlegen/umbenennen/löschen.
 * Niederlassungen/Standorte werden weiterhin auf der Organisationsseite gepflegt.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Network, Loader2, Plus, Trash2, Check, X, Pencil, Users } from 'lucide-react';
import { toast } from 'sonner';
import { companyAPI, usersAPI } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export const OrgStructureCard = () => {
  const [levels, setLevels] = useState([]);
  const [tree, setTree] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLevel, setNewLevel] = useState({ label: '', plural: '' });
  const [savingLevels, setSavingLevels] = useState(false);
  const [addingUnder, setAddingUnder] = useState(null); // unit id
  const [addName, setAddName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [managingId, setManagingId] = useState(null); // unit id for leadership editor
  const [selManagers, setSelManagers] = useState([]); // [{user_id, name}]
  const [savingMgr, setSavingMgr] = useState(false);

  const load = async () => {
    const res = await companyAPI.getOrgTree();
    setLevels(res.data?.levels || []);
    setTree(res.data?.tree || []);
  };

  useEffect(() => {
    Promise.all([
      load(),
      usersAPI.getAll().then((r) => setUsers(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const saveLevels = async (nextLevels) => {
    setSavingLevels(true);
    try {
      await companyAPI.updateOrgLevels({ levels: nextLevels });
      await load();
      toast.success('Ebenen gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingLevels(false);
    }
  };

  const addLevel = () => {
    if (!newLevel.label.trim()) return;
    const next = [...levels, {
      index: levels.length, key: `level_${Date.now()}`,
      label: newLevel.label.trim(), plural: (newLevel.plural.trim() || newLevel.label.trim()), system: false,
    }];
    setNewLevel({ label: '', plural: '' });
    saveLevels(next);
  };

  const removeLevel = (key) => saveLevels(levels.filter((l) => l.key !== key));

  const createUnit = async (parentId) => {
    if (!addName.trim()) return;
    try {
      await companyAPI.createOrgUnit({ parent_id: parentId, name: addName.trim() });
      setAddingUnder(null); setAddName('');
      await load();
      toast.success('Untereinheit angelegt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const renameUnit = async (id) => {
    try {
      await companyAPI.updateOrgUnit(id, { name: renameVal.trim() });
      setRenamingId(null); setRenameVal('');
      await load();
      toast.success('Umbenannt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Umbenennen fehlgeschlagen');
    }
  };

  const deleteUnit = async (id) => {
    try {
      await companyAPI.deleteOrgUnit(id);
      await load();
      toast.success('Einheit gelöscht');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const userName = (u) => `${u.last_name || ''}, ${u.first_name || ''}`.replace(/^, |, $/g, '') || u.email || u.id;

  const openManagers = (node) => {
    setManagingId(node.id);
    setSelManagers((node.managers || []).filter((m) => m.manual && m.user_id).map((m) => ({ user_id: m.user_id, name: m.name })));
  };

  const addManager = (userId) => {
    if (!userId || selManagers.some((m) => m.user_id === userId)) return;
    const u = users.find((x) => x.id === userId);
    setSelManagers((p) => [...p, { user_id: userId, name: u ? userName(u) : userId }]);
  };

  const removeManager = (userId) => setSelManagers((p) => p.filter((m) => m.user_id !== userId));

  const saveManagers = async (unitId) => {
    setSavingMgr(true);
    try {
      await companyAPI.setOrgUnitManagers(unitId, { managers: selManagers.map((m) => ({ user_id: m.user_id })) });
      setManagingId(null); setSelManagers([]);
      await load();
      toast.success('Leitung gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingMgr(false);
    }
  };

  const levelLabel = (idx) => levels[idx]?.label || `Ebene ${idx + 1}`;
  const hasDeeper = (node) => node.level_index + 1 < levels.length;

  const renderNode = (node, depth = 0) => (
    <div key={node.id} data-testid={`org-node-${node.id}`}>
      <div
        className="flex items-center gap-2 py-1.5 border-b border-border/40"
        style={{ paddingLeft: `${depth * 20}px` }}
      >
        <Badge variant="outline" className="text-[10px] shrink-0">{levelLabel(node.level_index)}</Badge>
        {renamingId === node.id ? (
          <div className="flex items-center gap-1 flex-1">
            <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-7 max-w-xs" data-testid={`org-rename-input-${node.id}`} />
            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => renameUnit(node.id)}><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setRenamingId(null)}><X className="h-4 w-4" /></Button>
          </div>
        ) : (
          <span className="text-sm font-medium flex-1 truncate">{node.name}</span>
        )}

        <div className="flex items-center gap-0.5 shrink-0">
          {(node.managers || []).length > 0 && (
            <span className="hidden sm:flex items-center gap-1 mr-1 text-[11px] text-muted-foreground">
              <Users className="h-3 w-3" />
              {node.managers.map((m) => m.name).join(', ')}
            </span>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openManagers(node)} data-testid={`org-managers-${node.id}`}>
            <Users className="h-3.5 w-3.5 mr-1" />Leitung
          </Button>
          {hasDeeper(node) && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAddingUnder(node.id); setAddName(''); }} data-testid={`org-add-child-${node.id}`}>
              <Plus className="h-3.5 w-3.5 mr-1" />{levelLabel(node.level_index + 1)}
            </Button>
          )}
          {node.source === 'manual' && renamingId !== node.id && (
            <>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setRenamingId(node.id); setRenameVal(node.name); }} data-testid={`org-rename-${node.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => deleteUnit(node.id)} data-testid={`org-delete-${node.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </>
          )}
        </div>
      </div>

      {managingId === node.id && (
        <div className="space-y-2 py-2" style={{ paddingLeft: `${(depth + 1) * 20}px` }} data-testid={`org-managers-editor-${node.id}`}>
          <div className="flex flex-wrap gap-1.5">
            {selManagers.length === 0 && <span className="text-xs text-muted-foreground italic">Noch keine (zusätzliche) Leitung.</span>}
            {selManagers.map((m) => (
              <Badge key={m.user_id} variant="secondary" className="text-xs gap-1 py-1" data-testid={`org-manager-chip-${node.id}-${m.user_id}`}>
                {m.name}
                <button onClick={() => removeManager(m.user_id)} className="ml-1 hover:text-rose-600"><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Select value="" onValueChange={addManager}>
              <SelectTrigger className="h-8 max-w-[260px]" data-testid={`org-manager-select-${node.id}`}>
                <SelectValue placeholder="Person hinzufügen…" />
              </SelectTrigger>
              <SelectContent>
                {users.filter((u) => !selManagers.some((m) => m.user_id === u.id)).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => saveManagers(node.id)} disabled={savingMgr} data-testid={`org-managers-save-${node.id}`}>
              {savingMgr ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setManagingId(null); setSelManagers([]); }}><X className="h-3.5 w-3.5" /></Button>
          </div>
          {node.source !== 'manual' && (
            <p className="text-[11px] text-muted-foreground">Ergänzt die auf der Organisationsseite gepflegte Leitung (z. B. Stellvertretung).</p>
          )}
        </div>
      )}

      {addingUnder === node.id && (
        <div className="flex items-center gap-1 py-1.5" style={{ paddingLeft: `${(depth + 1) * 20}px` }}>
          <Input
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder={`Name der neuen ${levelLabel(node.level_index + 1)}`}
            className="h-7 max-w-xs"
            data-testid={`org-add-input-${node.id}`}
            onKeyDown={(e) => { if (e.key === 'Enter') createUnit(node.id); }}
          />
          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => createUnit(node.id)} data-testid={`org-add-confirm-${node.id}`}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setAddingUnder(null)}><X className="h-4 w-4" /></Button>
        </div>
      )}

      {(node.children || []).map((c) => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <Card data-testid="org-structure-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Network className="h-5 w-5 text-primary" />
          Organisationsstruktur
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Ebenen definieren und den Org-Baum aufbauen. Niederlassungen &amp; Standorte
          werden auf der Organisationsseite gepflegt; hier ergänzen Sie tiefere Ebenen.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        ) : (
          <>
            {/* Ebenen */}
            <div className="space-y-2">
              <div className="text-sm font-medium">Ebenen (von oben nach unten)</div>
              <div className="flex flex-wrap items-center gap-2" data-testid="org-levels-list">
                {levels.map((l, i) => (
                  <Badge key={l.key} variant={l.system ? 'secondary' : 'outline'} className="text-xs gap-1.5 py-1">
                    <span className="opacity-60">{i + 1}.</span> {l.label}
                    {!l.system && (
                      <button onClick={() => removeLevel(l.key)} className="ml-1 hover:text-rose-600" data-testid={`org-level-remove-${l.key}`} disabled={savingLevels}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                {savingLevels && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Input value={newLevel.label} onChange={(e) => setNewLevel((p) => ({ ...p, label: e.target.value }))} placeholder="Neue Ebene (z. B. Abteilung)" className="h-8 max-w-[200px]" data-testid="org-new-level-label" />
                <Input value={newLevel.plural} onChange={(e) => setNewLevel((p) => ({ ...p, plural: e.target.value }))} placeholder="Mehrzahl (optional)" className="h-8 max-w-[200px]" data-testid="org-new-level-plural" />
                <Button size="sm" variant="outline" onClick={addLevel} disabled={savingLevels || !newLevel.label.trim()} data-testid="org-add-level-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Ebene
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Basisebenen (Niederlassung/Standort) sind fest. Eine Ebene lässt sich nur entfernen, wenn ihr keine Einheiten zugeordnet sind.</p>
            </div>

            {/* Baum */}
            <div className="space-y-1">
              <div className="text-sm font-medium">Org-Baum</div>
              <div className="rounded-md border p-2" data-testid="org-tree">
                {tree.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">Noch keine Einheiten.</p>
                ) : tree.map((n) => renderNode(n))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
